import * as React from 'react'

import { cn } from '../lib/cn'
import { IconImage } from '../lib/icons'

/** Identity imagery (family 10): a club logo, a portrait. Why `alt` is required in the
 *  types and what `''` means there: packages/ui/docs/nessuna-stringa-qui-dentro.md. */

export interface ThumbnailProps {
  /** Absent means "no image": the fallback renders instead. */
  src?: string
  /** The a11y label, or `''` for a decorative image next to its own name. */
  alt: string
  /** `md` stands alone (a card); `sm` fits dense contexts like table rows; `lg` is the
   * crest at the head of a page, where the club's own identity is the subject; `xl` the
   * crest of a club's hero on a wide screen, as tall as the lockup of names beside it. */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Drops the frame — border, surface and radius — leaving the image alone. For a crest
   * that IS the page's subject: a box around it reads as chrome the club did not put
   * there. Keep the frame in lists, where it holds the row's place before a logo loads. */
  bare?: boolean
  /** What to show without a src. Defaults to the placeholder-image icon. */
  fallback?: React.ReactNode
  className?: string
}

const SIZES = {
  sm: 'size-thumbnail-sm',
  md: 'size-thumbnail-md',
  lg: 'size-thumbnail-lg',
  xl: 'size-thumbnail-xl',
} as const

export function Thumbnail({
  src,
  alt,
  size = 'md',
  bare = false,
  fallback = <IconImage aria-hidden />,
  className,
}: ThumbnailProps) {
  // The frame is identical in both branches, so a logo appearing does not shift layout.
  const frame = cn(
    SIZES[size],
    'shrink-0 overflow-hidden',
    bare || 'rounded-md border border-input bg-background',
    className,
  )
  if (src === undefined) {
    const decorative = alt === ''
    return (
      <div
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : alt}
        aria-hidden={decorative || undefined}
        className={cn(frame, 'flex items-center justify-center text-muted-foreground')}
      >
        {fallback}
      </div>
    )
  }
  return <img src={src} alt={alt} className={cn(frame, 'object-contain')} />
}
